import type { ReactNode } from "react";
import Modal from "react-modal";
import PhaseSixIcon, { type TIconNames } from "../phaseSixIcon/PhaseSixIcon";
import { AreaButton, type ButtonColorVariant, ButtonVariant, IconPosition } from "../../complex/teacherArea/AreaButton";
import DesignConstants from "../../constants/DesignConstants";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";

export enum TitleColorVariants {
    DEFAULT = "DEFAULT",
    NEUTRAL = "NEUTRAL",
    PLANET_POP = "PLANET_POP",
    HIGH_CONTRAST = "HIGH_CONTRAST",
    DANGER = "DANGER",
    SECONDARY = "SECONDARY",
}

interface PopupButton {
    text: string;
    icon?: TIconNames;
    iconPosition?: IconPosition;
    onClick: () => void;
    disabled?: boolean;
    colorVariant?: ButtonColorVariant;
    className?: string;
}

interface BasePopupWrapperProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    titleColor: TitleColorVariants;
    titleIcon?: TIconNames;
    children: ReactNode;
    primaryButton?: PopupButton;
    secondaryButton?: PopupButton;
    maxWidth?: number;
    buttonLayout?: "end" | "between";
}

export const getModalStyles = (maxWidth?: number): Modal.Styles => ({
    overlay: {
        backgroundColor: DesignConstants.COLORS.TRANSPARENT_WHITE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "16px",
        overflow: "hidden",
    },
    content: {
        position: "relative",
        inset: "auto",
        padding: 0,
        border: DesignConstants.BORDERS.THIN_GREY,
        borderRadius: DesignConstants.BORDER_RADIUS.BUTTON,
        maxWidth: maxWidth ?? DesignConstants.BREAKPOINTS.LG,
        width: "90%",
        maxHeight: "90vh",
        background: DesignConstants.COLORS.WHITE,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        overscrollBehavior: "none",
    },
});

const titleColorClasses: Record<TitleColorVariants, string> = {
    [TitleColorVariants.DEFAULT]: "text-p6-orange",
    [TitleColorVariants.NEUTRAL]: "text-p6-grey-dark",
    [TitleColorVariants.PLANET_POP]: "text-pp-blue",
    [TitleColorVariants.HIGH_CONTRAST]: "text-p6-orange-darker",
    [TitleColorVariants.DANGER]: "text-p6-red",
    [TitleColorVariants.SECONDARY]: "text-forest-green-800",
};

const BasePopupWrapper = (props: BasePopupWrapperProps) => {
    const titleClass = titleColorClasses[props.titleColor];
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const hasButtons = props.secondaryButton || props.primaryButton;
    const justifyClass = props.buttonLayout === "between" ? "justify-between" : "justify-end";

    const renderButton = (button: PopupButton, variant: ButtonVariant) => (
        <AreaButton
            data-testid={variant === ButtonVariant.PRIMARY ? "popup-primary-button" : "popup-secondary-button"}
            variant={variant}
            colorVariant={button.colorVariant}
            role="button"
            onClick={button.onClick}
            iconName={button.icon}
            iconPosition={button.iconPosition}
            disabled={button.disabled}
            className={button.className}
        >
            {button.text}
        </AreaButton>
    );

    return (
        <Modal
            isOpen={props.isOpen}
            onRequestClose={props.onClose}
            style={getModalStyles(props.maxWidth)}
            ariaHideApp={false}
        >
            <div className="flex flex-col flex-1 min-h-0 gap-s p-m">
                <div className="flex gap-s items-center shrink-0">
                    {props.titleIcon && (
                        <PhaseSixIcon
                            name={props.titleIcon}
                            className={`text-header-icon ${titleClass}`}
                        />
                    )}
                    <h2 className={`text-xl ${titleClass} font-normal`}>{props.title}</h2>
                </div>

                <div className="flex-1 overflow-visible">{props.children}</div>

                {hasButtons && (
                    <div
                        className={`flex gap-s shrink-0 ${getStyleBasedOnBreakpoint(
                            Breakpoint.SM,
                            `flex-row items-center ${justifyClass}`,
                            "flex-col w-full [&>*]:w-full"
                        )}`}
                    >
                        {props.secondaryButton && renderButton(props.secondaryButton, ButtonVariant.SECONDARY)}
                        {props.primaryButton && renderButton(props.primaryButton, ButtonVariant.PRIMARY)}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default BasePopupWrapper;
